/**
 * Onset-autocorrelation BPM detector.
 *
 * Used as a fallback in the Slicer when the user hasn't typed in
 * a bar count. The primary path ("I know this is 4 bars") gives
 * exact BPM via `(bars × 4 × 60) / durationSec`; this function
 * is what powers the "Auto-detect" button for samples whose loop
 * length isn't known.
 *
 * Algorithm:
 *   1. Frame the audio into short overlapping windows and compute
 *      RMS energy per frame.
 *   2. Half-wave-rectified differentiate the RMS series to produce
 *      an onset envelope — spikes when energy rises (a new kick,
 *      snare, or hat hit). A centered (mean-subtracted) copy is
 *      used for autocorrelation so a non-zero DC floor doesn't
 *      inflate correlations uniformly across lags.
 *   3. Normalized cross-correlation at every integer frame lag in
 *      the 60..200 BPM range. Bounded in [-1, 1] and directly
 *      comparable across lags — short integration windows (long
 *      lags) no longer ride higher just because we're dividing by
 *      a smaller N.
 *   4. Parabolic interpolation around the peak lag, giving
 *      sub-frame precision. Without this, the detector rounds to
 *      whatever integer lag is closest — at ~170 BPM the frame-
 *      lag resolution is ~3 BPM per step, so a genuine 170 lands
 *      as 169 or 172 by pure quantization. Parabolic refinement
 *      recovers that within 0.2 BPM.
 *   5. Resolve octave ambiguity with a bias toward the 95..190
 *      BPM working band. Upshift if the detected tempo is below
 *      95 and the double-tempo peak is ≥ 70% of the best (eager,
 *      since half-time locks are the more common failure mode for
 *      dense drum material). Downshift if above 190 and the
 *      half-tempo peak is ≥ 92% (tight — 200+ BPM is rare and
 *      over-aggressive halving drops genuine breaks into slow 60s
 *      territory). Users can always flip via the ½× / 2× toggle
 *      in the slicer UI if they want the other octave.
 *
 * Works well for breakbeats, house/techno drums, and anything with
 * transient-heavy onset structure. Weaker on slow pads and vocal
 * loops — the confidence score reflects that, and the user can
 * always override via the BPM input.
 *
 * Kept pure (no Web Audio, no DOM) so it can run in a Worker.
 */

export interface DetectedBPM {
  /** Best-guess tempo in BPM, clamped to the search range. */
  bpm: number
  /** 0..1 — how much the peak correlation stands out above the
   *  mean. Low confidence (<0.4) is the UI's cue to surface a
   *  warning and invite the user to override. */
  confidence: number
  /**
   * Half-wave-rectified onset envelope — one value per audio frame
   * (where a frame spans HOP_SIZE samples). Exposed so callers
   * (the slicer in particular) can run their own peak detection
   * on the same signal without recomputing from raw audio. Spikes
   * here correspond to transients (kicks, snares, hats).
   */
  onsetEnvelope: Float32Array
  /**
   * Frame rate of the onset envelope in Hz. Multiply a frame index
   * by `1 / frameRate` to convert to seconds. Equal to
   * `sampleRate / HOP_SIZE`.
   */
  frameRate: number
}

const WINDOW_SIZE = 1024
const HOP_SIZE = 256
const MIN_BPM = 60
const MAX_BPM = 200

/**
 * Runs detection on a mono PCM buffer. Caller should pass the left
 * channel of a stereo sample (drum energy is typically symmetric
 * enough that the extra channel is wasted work).
 */
export function detectBPM(
  samples: Float32Array,
  sampleRate: number,
): DetectedBPM {
  if (samples.length < WINDOW_SIZE * 4 || sampleRate <= 0) {
    return {
      bpm: 120,
      confidence: 0,
      onsetEnvelope: new Float32Array(0),
      frameRate: sampleRate > 0 ? sampleRate / HOP_SIZE : 0,
    }
  }

  const frameRate = sampleRate / HOP_SIZE
  const numFrames = Math.floor((samples.length - WINDOW_SIZE) / HOP_SIZE) + 1

  // Step 1: RMS energy per frame.
  const rms = new Float32Array(numFrames)
  for (let i = 0; i < numFrames; i++) {
    const start = i * HOP_SIZE
    let sum = 0
    for (let j = 0; j < WINDOW_SIZE; j++) {
      const x = samples[start + j]
      sum += x * x
    }
    rms[i] = Math.sqrt(sum / WINDOW_SIZE)
  }

  // Step 2: half-wave-rectified onset envelope (the public signal)
  // + a mean-centered copy for autocorrelation. Centering matters:
  // every non-silent sample has a non-zero floor on the onset
  // envelope (because HWR diff is non-negative), and an uncentered
  // autocorrelation reads that DC as periodic structure, biasing
  // long lags upward. Centered envelope = cleaner periodicity signal.
  const onset = new Float32Array(numFrames)
  for (let i = 1; i < numFrames; i++) {
    const diff = rms[i] - rms[i - 1]
    onset[i] = diff > 0 ? diff : 0
  }
  let onsetMean = 0
  for (let i = 0; i < numFrames; i++) onsetMean += onset[i]
  onsetMean /= numFrames
  const centered = new Float32Array(numFrames)
  for (let i = 0; i < numFrames; i++) centered[i] = onset[i] - onsetMean

  // Step 3: integer-lag autocorrelation. Working in lag space
  // rather than BPM space gives one correlation value per frame-
  // lag — no risk of multiple BPMs aliasing onto the same lag, and
  // the natural unit for later parabolic refinement.
  const lagMin = Math.max(1, Math.floor((60 / MAX_BPM) * frameRate))
  const lagMax = Math.min(numFrames - 2, Math.ceil((60 / MIN_BPM) * frameRate))
  if (lagMax <= lagMin + 1) {
    return {
      bpm: 120,
      confidence: 0,
      onsetEnvelope: onset,
      frameRate,
    }
  }

  // Normalized cross-correlation: cross / sqrt(E_a * E_b). Bounded
  // in [-1, 1] and — unlike the raw-sum-divided-by-N we used before
  // — not biased by integration-window size. Two lags measured over
  // different window lengths are now directly comparable.
  const corrByLag = new Float32Array(lagMax + 1)
  let bestLag = lagMin
  let bestCorr = -Infinity
  for (let lag = lagMin; lag <= lagMax; lag++) {
    let cross = 0
    let eA = 0
    let eB = 0
    const end = numFrames - lag
    for (let i = 0; i < end; i++) {
      const a = centered[i]
      const b = centered[i + lag]
      cross += a * b
      eA += a * a
      eB += b * b
    }
    const norm = Math.sqrt(eA * eB)
    const corr = norm > 0 ? cross / norm : 0
    corrByLag[lag] = corr
    if (corr > bestCorr) {
      bestCorr = corr
      bestLag = lag
    }
  }

  // Step 4: octave disambiguation. Preferred band is 95..190 BPM
  // — the range where DnB / jungle / hip-hop / house all feel
  // "read as-is". Below 95, a true tempo above 160 often locks
  // onto the backbeat (snare on 3) and reads at half-time. So
  // if the detected tempo is sub-95 AND its double-tempo peak is
  // at all competitive, prefer the double. Above 190 the symmetric
  // move is to halve. User can always flip via the ½× / 2× toggle
  // in the slicer UI; this is just a less-biased default.
  //
  // Thresholds: `< 95` with a 0.7 correlation ratio means we're
  // fairly eager to upshift (half-time is the more common failure
  // mode than octave-up false positives). `> 190` stays tight at
  // 0.92 since 200-BPM breaks are rare and over-aggressive halving
  // bumps them into slow-60s territory.
  const integerBPM = Math.round((60 * frameRate) / bestLag)
  if (integerBPM < 95) {
    const halfLag = Math.round(bestLag / 2) // double the BPM
    if (
      halfLag >= lagMin &&
      halfLag <= lagMax &&
      corrByLag[halfLag] >= bestCorr * 0.7
    ) {
      bestLag = halfLag
      bestCorr = corrByLag[halfLag]
    }
  } else if (integerBPM > 190) {
    const doubleLag = bestLag * 2 // halve the BPM
    if (
      doubleLag >= lagMin &&
      doubleLag <= lagMax &&
      corrByLag[doubleLag] >= bestCorr * 0.92
    ) {
      bestLag = doubleLag
      bestCorr = corrByLag[doubleLag]
    }
  }

  // Step 5: parabolic interpolation. At ~170 BPM, integer frame-
  // lags are ~3 BPM apart — `bestLag` rounds the true peak to the
  // nearest frame, which for jungle / DnB material is the
  // difference between reading 170 vs 169 or 172. Fitting a
  // parabola through (bestLag-1, bestLag, bestLag+1) and solving
  // for the vertex gives us a sub-frame refined lag and brings
  // rounded-integer BPM within ~0.2 of truth.
  let refinedLag = bestLag
  if (bestLag > lagMin && bestLag < lagMax) {
    const y1 = corrByLag[bestLag - 1]
    const y2 = corrByLag[bestLag]
    const y3 = corrByLag[bestLag + 1]
    const denom = y1 - 2 * y2 + y3
    if (denom !== 0) {
      const delta = (0.5 * (y1 - y3)) / denom
      // A |delta| > 1 means the parabola fit is off (e.g. the peak
      // is actually at an adjacent frame because the discrete max
      // grabbed a sidelobe). Fall back to the integer pick.
      if (Math.abs(delta) < 1) refinedLag = bestLag + delta
    }
  }

  const rawBPM = (60 * frameRate) / refinedLag
  const bestBPM = Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(rawBPM)))

  // Confidence — how far above the mean the winning correlation
  // sits, normalized by the headroom between mean and perfect
  // correlation (1.0). With normalized cross-correlation every
  // value is in [-1, 1] so this number has a stable physical
  // meaning across samples — 0.8 really is "much more periodic
  // than average", not an artifact of loud input.
  let sumCorr = 0
  let count = 0
  for (let lag = lagMin; lag <= lagMax; lag++) {
    sumCorr += corrByLag[lag]
    count++
  }
  const meanCorr = count > 0 ? sumCorr / count : 0
  const headroom = Math.max(0.01, 1 - meanCorr)
  const confidence = Math.max(0, Math.min(1, (bestCorr - meanCorr) / headroom))

  return {
    bpm: bestBPM,
    confidence,
    onsetEnvelope: onset,
    frameRate,
  }
}

/**
 * Find local maxima in an onset envelope above a relative threshold.
 * Used by the slicer for peak-aware chopping (snap N slices to N
 * detected transients instead of dividing the duration equally).
 *
 *  - `threshold` is a fraction of the envelope's max (0.3 = 30%);
 *    peaks below it are suppressed as noise.
 *  - `minSpacingFrames` enforces a minimum gap between detected
 *    peaks so a single transient with a few-frame ringing tail
 *    doesn't surface as multiple "peaks." Caller-tunable; the
 *    slicer uses ~50ms worth of frames.
 *
 * Returns peak frame indices sorted ascending. Convert to seconds
 * by multiplying by `1 / frameRate`.
 */
export function findOnsetPeaks(
  onset: Float32Array,
  threshold = 0.3,
  minSpacingFrames = 0,
): number[] {
  if (onset.length === 0) return []
  let max = 0
  for (let i = 0; i < onset.length; i++) {
    if (onset[i] > max) max = onset[i]
  }
  if (max === 0) return []
  const cutoff = max * threshold
  const peaks: number[] = []
  let lastPeakIdx = -Infinity
  for (let i = 1; i < onset.length - 1; i++) {
    const v = onset[i]
    if (v < cutoff) continue
    if (v <= onset[i - 1] || v < onset[i + 1]) continue
    if (i - lastPeakIdx < minSpacingFrames) {
      // Within the suppression window — keep whichever is taller.
      if (v > onset[lastPeakIdx]) {
        peaks[peaks.length - 1] = i
        lastPeakIdx = i
      }
      continue
    }
    peaks.push(i)
    lastPeakIdx = i
  }
  return peaks
}
